<template>
    <div class="w-full h-full">
        <div class="grid grid-cols-[1fr_auto_1fr] items-center pb-2">
            <div></div>
            <h3 class="text-center font-semibold">{{ $t('views.reports.selectContentOptionsPage.subheader') }}</h3>
            <button
                type="button"
                class="justify-self-end text-sm font-semibold text-primary-700 hover:text-primary-800 transition-colors disabled:text-gray-400 disabled:cursor-default"
                :disabled="allOptionIds.length === 0"
                @click="toggleAll"
            >
                {{ allSelected ? $t('views.reports.selectContentOptionsPage.deselectAllButtonLabel') : $t('views.reports.selectContentOptionsPage.selectAllButtonLabel') }}
            </button>
        </div>
        <div :class="['border-2 h-full overflow-y-auto border-opacity-10 rounded-lg py-2', maxHeightClass]">
            <div class="flex flex-col">
                <ContentOptionsNode
                    v-for="(root, index) of optionTree"
                    :key="root.id"
                    :option="root"
                    :level="0"
                    :selected-ids="computedState.contentOptionIds"
                    :expanded-ids="expandedOptionIds"
                    :indeterminate-ids="indeterminateOptionIds"
                    :is-last="index === optionTree.length - 1"
                    @toggle-option="toggleOption"
                    @toggle-expand="toggleExpandedById"
                ></ContentOptionsNode>
            </div>
        </div>
    </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import ContentOptionsNode from '@/views/dashboard/reports/selectContentOptions/ContentOptionsNode.vue';
import { store } from '@/plugins/store';
import { ActionType } from '@/plugins/store/actions';
import { ReportContentOption, ReportViewState } from '@/types';

type ContentOptionNodeType = ReportContentOption & { children: ContentOptionNodeType[] };

type FlattenedOption = {
    option: ContentOptionNodeType;
    level: number;
    ancestors: number[];
};

export default defineComponent({
    components: { ContentOptionsNode },
    props: {
        state: {
            type: Object as () => ReportViewState,
            required: true,
        },
        contentOptions: {
            type: Array as () => ReportContentOption[],
            default: () => [],
        },
        autoSelectAll: {
            type: Boolean,
            default: false,
        },
        maxHeightClass: {
            type: String,
            default: 'max-h-[50rem]',
        },
    },
    emits: ['update:state'],
    data() {
        return {
            loadedContentOptions: [] as ReportContentOption[],
            expandedOptionIds: [] as number[],
        };
    },
    computed: {
        computedState: {
            get(): ReportViewState {
                return this.$props.state;
            },
            set(newValue: ReportViewState) {
                this.$emit('update:state', newValue);
            },
        },
        resolvedOptions(): ReportContentOption[] {
            return this.contentOptions.length ? this.contentOptions : this.loadedContentOptions;
        },
        activeOptions(): ReportContentOption[] {
            return this.resolvedOptions.filter((option) => option.is_active).sort((a, b) => a.sort_order - b.sort_order);
        },
        optionTree(): ContentOptionNodeType[] {
            const nodes = this.activeOptions.map((option) => ({ ...option, children: [] as ContentOptionNodeType[] }));
            const nodeMap = new Map<number, ContentOptionNodeType>();
            const roots: ContentOptionNodeType[] = [];

            nodes.forEach((node) => nodeMap.set(node.id, node));

            nodes.forEach((node) => {
                if (node.parent_option_id === null) {
                    roots.push(node);
                    return;
                }

                const parent = nodeMap.get(node.parent_option_id);
                if (parent) {
                    parent.children.push(node);
                }
            });

            const sortNodes = (list: ContentOptionNodeType[]) => {
                list.sort((a, b) => a.sort_order - b.sort_order);
                list.forEach((child) => sortNodes(child.children));
            };

            sortNodes(roots);
            return roots;
        },
        flattenedOptions(): FlattenedOption[] {
            const flattened: FlattenedOption[] = [];
            const walk = (node: ContentOptionNodeType, level: number, ancestors: number[]) => {
                flattened.push({ option: node, level, ancestors });
                node.children.forEach((child) => walk(child, level + 1, [...ancestors, node.id]));
            };

            this.optionTree.forEach((root) => walk(root, 0, []));
            return flattened;
        },
        allOptionIds(): number[] {
            return this.flattenedOptions.map((entry) => entry.option.id);
        },
        allSelected(): boolean {
            if (!this.allOptionIds.length) {
                return false;
            }

            const selectedSet = new Set(this.computedState.contentOptionIds);
            return this.allOptionIds.every((id) => selectedSet.has(id));
        },
        indeterminateOptionIds(): number[] {
            const selectedSet = new Set(this.computedState.contentOptionIds);
            const indeterminateIds: number[] = [];

            const checkNode = (node: ContentOptionNodeType): { total: number; selected: number } => {
                if (!node.children.length) {
                    return { total: 1, selected: selectedSet.has(node.id) ? 1 : 0 };
                }

                let totalDescendants = 0;
                let selectedDescendants = 0;

                node.children.forEach((child) => {
                    const childResult = checkNode(child);
                    totalDescendants += childResult.total;
                    selectedDescendants += childResult.selected;
                });

                if (selectedDescendants > 0 && selectedDescendants < totalDescendants) {
                    indeterminateIds.push(node.id);
                }

                return { total: totalDescendants, selected: selectedDescendants };
            };

            this.optionTree.forEach((root) => checkNode(root));
            return indeterminateIds;
        },
        parentIdMap(): Record<number, number | null> {
            const map: Record<number, number | null> = {};
            this.activeOptions.forEach((option) => {
                map[option.id] = option.parent_option_id;
            });
            return map;
        },
    },
    watch: {
        contentOptions(newValue: ReportContentOption[]) {
            if (!newValue.length) {
                return;
            }

            this.initializeOptions(newValue);
        },
    },
    created() {
        if (this.contentOptions.length) {
            this.initializeOptions(this.contentOptions);
            return;
        }

        store.dispatch(ActionType.GetReportContentOptions).then((options: ReportContentOption[]) => {
            this.initializeOptions(options);
        });
    },
    methods: {
        initializeOptions(options: ReportContentOption[]) {
            this.loadedContentOptions = options;
            this.syncSelection();
            const didApplyDefaultSelection = this.applyDefaultSelection();
            if (!didApplyDefaultSelection) {
                this.syncExpandedFromSelection();
            }
        },
        applyDefaultSelection(): boolean {
            if (!this.autoSelectAll || this.computedState.contentOptionIds.length || this.computedState.contentOptionsTouched) {
                return false;
            }

            const activeIds = this.activeOptions.map((option) => option.id);
            this.computedState.contentOptionIds.splice(0, this.computedState.contentOptionIds.length, ...activeIds);
            return true;
        },
        syncSelection() {
            const activeIds = new Set(this.activeOptions.map((option) => option.id));
            const filtered = this.computedState.contentOptionIds.filter((id) => activeIds.has(id));

            if (filtered.length === this.computedState.contentOptionIds.length) {
                return;
            }

            this.computedState.contentOptionIds.splice(0, this.computedState.contentOptionIds.length, ...filtered);
        },
        syncExpandedFromSelection() {
            const expandedSet = new Set(this.expandedOptionIds);
            this.computedState.contentOptionIds.forEach((id) => {
                this.getAncestorIds(id).forEach((ancestorId) => expandedSet.add(ancestorId));
            });
            this.expandedOptionIds.splice(0, this.expandedOptionIds.length, ...Array.from(expandedSet));
        },
        toggleExpandedById(optionId: number) {
            if (this.expandedOptionIds.includes(optionId)) {
                this.expandedOptionIds = this.expandedOptionIds.filter((id) => id !== optionId);
            } else {
                this.expandedOptionIds = [...this.expandedOptionIds, optionId];
            }
        },
        getAncestorIds(optionId: number): number[] {
            const ancestors: number[] = [];
            let parentId = this.parentIdMap[optionId];
            while (parentId) {
                ancestors.push(parentId);
                parentId = this.parentIdMap[parentId];
            }
            return ancestors;
        },
        collectDescendantIds(option: ContentOptionNodeType): number[] {
            const ids: number[] = [];
            const walk = (node: ContentOptionNodeType) => {
                ids.push(node.id);
                node.children.forEach((child) => walk(child));
            };

            option.children.forEach((child) => walk(child));
            return ids;
        },
        addIds(ids: number[]) {
            const selectedSet = new Set(this.computedState.contentOptionIds);
            ids.forEach((id) => selectedSet.add(id));
            this.computedState.contentOptionIds.splice(0, this.computedState.contentOptionIds.length, ...Array.from(selectedSet));
        },
        removeIds(ids: number[]) {
            const idsSet = new Set(ids);
            const filtered = this.computedState.contentOptionIds.filter((id) => !idsSet.has(id));
            this.computedState.contentOptionIds.splice(0, this.computedState.contentOptionIds.length, ...filtered);
        },
        toggleOption(option: ContentOptionNodeType) {
            this.computedState.contentOptionsTouched = true;
            const ids = [option.id, ...this.collectDescendantIds(option)];
            const selectedSet = new Set(this.computedState.contentOptionIds);
            const everySelected = ids.every((id) => selectedSet.has(id));

            if (everySelected) {
                this.removeIds(ids);
            } else {
                this.addIds(ids);
            }
        },
        toggleAll() {
            this.computedState.contentOptionsTouched = true;
            if (this.allSelected) {
                this.computedState.contentOptionIds.splice(0, this.computedState.contentOptionIds.length);
                return;
            }

            this.computedState.contentOptionIds.splice(0, this.computedState.contentOptionIds.length, ...this.allOptionIds);
        },
    },
});
</script>
